import { useEffect, useRef } from "react";
import { useUserIsAdmin, useUserLoggedInStatus } from "./user";
import { useQueryClient } from "react-query";
import ReconnectingWebSocket from "reconnecting-websocket";
import { domain as baseUrl } from "./rest";
import {
  commentEmissionHandler,
  commonEmissionHandler,
  emissionHandler,
} from "../../modules/emissions/useEmissions";
import { scenariosHandler } from "../../modules/emissions/scenarioComponents/useScenarios";
import { jobsHandler } from "../../modules/emissions/scenarioComponents/useJobs";
import { debounce } from "../global";
import {
  portfolioHandler,
  portfolioPeriodScopesHandler,
} from "../../modules/emissions/portfolio_helper";
import { displayNotificationToast } from "../../modules/notifications/NotificationToast";
import { useFeatureFlags } from "../customHoooks/useFeatureFlags";
import { buildTradableProductParams } from "../customHoooks/useTradableProducts";

const snapshotDebounceRef = { current: null };
const snapshotDebounceTimeoutRef = { current: null };

let tradableProductIdsToProcess = [];

const handleWebsocketMessage = (event, queryClient, isAdmin) => {
  const data = JSON.parse(event.data);

  const livePriceHandler = () => {
    if (!data.channel.startsWith("dispatch_price")) return;
    queryClient.invalidateQueries("livePrices");
  };

  const updateTradableProductWithTargetedId = async (
    tradableProductIds,
    queryClient
  ) => {
    try {
      const cachedProducts = queryClient.getQueriesData({
        predicate: (query) => {
          return query.queryKey[0].startsWith("tradableProducts");
        },
      });

      if (!cachedProducts) return;

      const newProductData = await queryClient.fetchQuery({
        queryKey: [
          "fetchTradableProducts",
          {
            action: "fetchTradableProducts",
            params: buildTradableProductParams({
              isAdmin: isAdmin,
              productIds: tradableProductIds,
              includeProductRules: true,
              includeDailyTradeHistories: true,
            }),
            enabled: true,
          },
        ],
        staleTime: 0,
      });

      if (!newProductData.data.length) return;

      const newDatas = newProductData.data;

      queryClient.setQueriesData(
        {
          predicate: (query) => {
            if (!query.queryKey[0].startsWith("tradableProducts")) return false;
            const isIncludingTradableProduct = query.state?.data?.data?.some(
              (r) => tradableProductIds.some((id) => r.id === id)
            );

            return isIncludingTradableProduct;
          },
        },
        (oldData) => {
          if (!oldData?.data) return oldData;

          const isQueryInFocus = oldData.config.url.includes(
            "in_focus_tradable_products"
          );
          const isQueryMarketSnapshots = oldData.config.url.includes(
            "bloomberg_tradable_products"
          );
          const isQueryInLiveProducts = oldData.config.url.includes(
            "in_live_tradable_products"
          );

          return {
            ...oldData,
            data: oldData.data.reduce((acc, currentItem) => {
              if (!tradableProductIds.some((id) => id === currentItem.id))
                return [...acc, currentItem];

              const newItem = newDatas.find(
                (data) => data.id === currentItem.id
              );
              const removeFromInFocus =
                isQueryInFocus &&
                !newItem.snapshots.inFocus &&
                currentItem.snapshots.inFocus;
              const removeFromMS =
                isQueryMarketSnapshots &&
                !newItem.snapshots.isMarketsSnapshot &&
                currentItem.snapshots.isMarketsSnapshot;
              const removeFromInLive =
                isQueryInLiveProducts &&
                !newItem.snapshots.inLive &&
                currentItem.snapshots.inLive;

              if (removeFromInFocus || removeFromMS || removeFromInLive)
                return acc;

              return [...acc, { ...currentItem, ...newItem }];
            }, []),
          };
        }
      );

      tradableProductIds.forEach((tradableProductId) => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            if (!query.queryKey[0].startsWith("tradableProducts")) return false;

            const isIncludingTradableProduct = query.state.data.data.some(
              (r) => r.id === tradableProductId
            );

            if (isIncludingTradableProduct) return false;

            const newItem = newDatas.find(
              (data) => data.id === tradableProductId
            );
            const params = query.queryKey[1].params;
            const inFocusNeedUpdate =
              params.select.includes("in_focus_tradable_products") &&
              params.product_class?.includes(newItem.productClass) &&
              newItem.snapshots.inFocus;
            const msNeedUpdate =
              params.select.includes("bloomberg_tradable_products") &&
              (params.product_class?.includes(newItem.productClass) ||
                params.product_type?.includes(newItem.productType)) &&
              newItem.snapshots.isMarketsSnapshot;
            const inLiveNeedUpdate =
              params.select.includes("in_live_tradable_products") &&
              params.product_class?.includes(newItem.productClass) &&
              (!("tenor_type" in params)
                ? true
                : params.tenor_type?.includes(newItem.snapshots.tenorType)) &&
              newItem.snapshots.inLive;

            return inFocusNeedUpdate || msNeedUpdate || inLiveNeedUpdate;
          },
        });
      });
    } catch (error) {
      console.error("Error fetching tradable product:", error);
      // Fallback to invalidation if fetch fails
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!query.queryKey[0].startsWith("tradableProducts")) return false;
          const isIncludingTradableProduct = query.state?.data?.data?.some(
            (r) => tradableProductIds.some((id) => r.id === id)
          );

          return isIncludingTradableProduct;
        },
      });
    }
  };

  const snapshotsAndTradableProductRulesHandler = () => {
    const {
      channel,
      payload: {
        row: { tradable_product: tradableProductId },
      },
    } = data;

    if (
      !channel.startsWith("snapshot_detail") &&
      !channel.startsWith("snapshot") &&
      !channel.startsWith("tradable_product_rule-admin")
    ) {
      return;
    }

    tradableProductIdsToProcess = Array.from(
      new Set([...tradableProductIdsToProcess, tradableProductId])
    );

    debounce(
      snapshotDebounceRef,
      () => {
        updateTradableProductWithTargetedId(
          tradableProductIdsToProcess,
          queryClient
        );
        // The old snapshot API
        queryClient.invalidateQueries({
          predicate: (query) => {
            const isSnapshotAPI = ["snapshots_detail", "snapshots"].includes(
              query.queryKey[0]
            );
            if (!isSnapshotAPI) return false;

            const isSelectingTradableProduct =
              query.queryKey[1]?.params?.tradable_products?.includes(
                tradableProductId
              );

            if (isSelectingTradableProduct) {
              return true;
            }

            const responses = query.state?.data?.data;
            const isIncludingTradableProduct = responses?.some(
              (r) => r.id === tradableProductId
            );

            return responses && isIncludingTradableProduct;
          },
        });

        // Fetcing tradable products to ensure that the markets snapshot BB items are updated
        // when a market snapshot is added or updated.
        // Keep the Bloomberg products query invalidation
        queryClient.invalidateQueries("getBloombergTradableProducts");
        tradableProductIdsToProcess = [];
      },
      500,
      snapshotDebounceTimeoutRef,
      5000
    );
  };

  const companyHandler = () => {
    if (!data.channel.startsWith("company")) return;
    queryClient.invalidateQueries("companyData");
  };

  const tradeTransactionHandler = () => {
    if (!data.channel.startsWith("rehub_trade_transaction")) return;
    queryClient.invalidateQueries("rehub_trade_logs");
    queryClient.invalidateQueries("rehubTradeTimelines");
  };

  const apPriceCurveResultsHandler = () => {
    if (!data.channel.startsWith("ap_price_curve_results")) return;
    queryClient.invalidateQueries("apPriceCurveResults");
  };

  const fileUploadHandler = () => {
    if (!data.channel.startsWith("file-user-")) return;
    const key = "fileJobStatus";
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === key,
    });
  };

  const certificateProjectAdminHandler = () => {
    if (!data.channel.startsWith("certificate_project-admin")) return;
    queryClient.invalidateQueries("certificateProjects");
  };

  const wsHandlers = [
    livePriceHandler,
    snapshotsAndTradableProductRulesHandler,
    companyHandler,
    tradeTransactionHandler,
    apPriceCurveResultsHandler,
    fileUploadHandler,
    certificateProjectAdminHandler,
    () => scenariosHandler(data, queryClient),
    () => portfolioHandler(data, queryClient),
    () => emissionHandler(data, queryClient),
    () => portfolioPeriodScopesHandler(data, queryClient),
    () => jobsHandler(data, queryClient),
    () => commonEmissionHandler(data, "offset-company-", "offsets"),
    () => commonEmissionHandler(data, "offset-file-company-", "offsetFiles"),
    () => commonEmissionHandler(data, "file-company-", "offsetFiles"),
    () => commonEmissionHandler(data, "initiative-company-", "initiatives"),
    () =>
      commentEmissionHandler(
        data,
        "offset-comment-company-",
        "offsetComments",
        "emissionOffset"
      ),
    () =>
      commentEmissionHandler(
        data,
        "initiative-comment-company-",
        "initiativeComments",
        "initiative"
      ),
    () =>
      commonEmissionHandler(
        data,
        "initiative-file-company-",
        "initiativeFiles"
      ),
  ];

  wsHandlers.forEach((handler) => handler());
};

const handleNotificationMessage = (
  event,
  queryClient,
  features,
  onNavigate
) => {
  const data = JSON.parse(event.data);

  if (!data.channel.startsWith("notification")) return;
  if (!features?.includes("notifications")) return;
  queryClient.invalidateQueries("getNotification");
  const {
    payload: { row: payload },
  } = data;

  displayNotificationToast({
    payload: payload,
    queryClient: queryClient,
    onNavigate,
  });
};

const wsURL = () => {
  const domainWithProtocol = baseUrl();
  const domainURL = new URL(domainWithProtocol);
  const domain = domainURL.host;
  const protocol = domainURL.protocol;
  return `${protocol === "https:" ? "wss://" : "ws://"}${domain}/ws/`;
};

export const useReactQuerySubscription = (onNavigate) => {
  const isAdmin = useUserIsAdmin();
  const isLoggedIn = useUserLoggedInStatus();
  const queryClient = useQueryClient();
  const { features } = useFeatureFlags();

  const queryClientRef = useRef(queryClient);
  const isAdminRef = useRef(isAdmin);
  const featuresRef = useRef(features);
  const onNavigateRef = useRef(onNavigate);

  useEffect(() => {
    queryClientRef.current = queryClient;
    isAdminRef.current = isAdmin;
    featuresRef.current = features;
    onNavigateRef.current = onNavigate;
  }, [queryClient, isAdmin, features, onNavigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const websocket = new ReconnectingWebSocket(wsURL());
    websocket.onmessage = (event) => {
      handleWebsocketMessage(event, queryClientRef.current, isAdminRef.current);
      handleNotificationMessage(
        event,
        queryClientRef.current,
        featuresRef.current,
        onNavigateRef.current
      );
    };
    return () => {
      websocket.close();
    };
  }, [isLoggedIn]);
};
